import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { enterSpace } from '~src/app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '~src/app/actions/spaces/FavoritesActions';
import { openContentDetailDialog } from '~src/app/actions/spaces/OpenContentDetailDialogActions';
import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '~src/app/content/breadcrumbs/BreadcrumbHelpers';
import { usePrevious } from '~src/app/hooks';
import { SessionSiteSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { AppState } from '~src/app/state/AppState';
import { SpaceInfo } from '~src/app/state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { BrowserTitleService } from '~src/commonInfra/services/BrowserTitleService';
import { ISanitizer, sanitize } from '~src/commonInfra/utils/Sanitize';
import { Projects, ProjectsResource } from '~src/projects/resources/Projects';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { CommonErrorCode } from '~src/ts/helpers/ErrorCodes';
import { Filter } from '~src/ts/helpers/Filter';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IProjectDetailInfo, IProjectInfo } from '~src/ts/models/IProjectInfo';
import { IFavoritableItemInfo } from '~src/ts/models/resourceInfoParts';
import { IProjectActions } from '~src/ts/ServerApi/types/IProjectActions';

import {
  ConnectedQueryParamUpdateProvider,
  QueryParamUpdateProviderOwnProps
} from '../RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedProjectSpace } from './ConnectedProjectSpace';
import { ProjectSpaceOwnProps } from './ProjectSpace';


export interface ProjectSpaceProviderOwnProps {
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly projectId: string;
  readonly urls: SpacesSiteUrls;
  readonly renderProjectSpace: (props: ProjectSpaceOwnProps) => JSX.Element;
  readonly setFetchedAllData: (isFetched: boolean) => void;
}

export interface ProjectSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly openContentDetailDialog: (content: IContentInfo, availableActions: IProjectActions) => void;
}

export interface ProjectSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export interface ProjectSpaceProviderPropsFromServices {
  readonly Projects: ProjectsResource;
  readonly sanitizer: ISanitizer;
}

export type ProjectSpaceNonServicesProps = ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps & ProjectSpaceProviderDispatchProps;

export type ProjectSpaceProviderContainerProps =  ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps &  ProjectSpaceProviderDispatchProps & ProjectSpaceProviderPropsFromServices;

export interface ProjectSpaceProviderContainerState {
  readonly projectInfo: IProjectInfo;
  readonly projectDetail: IProjectDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly projectActions: IProjectActions;
  readonly errorCode: number;
}

const InitialState: ProjectSpaceProviderContainerState = {
  projectInfo: undefined,
  projectDetail: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  projectActions: undefined,
  errorCode: undefined
};

export class ProjectSpaceProviderContainer extends React.PureComponent<ProjectSpaceProviderContainerProps, ProjectSpaceProviderContainerState> {

  private unmounted: boolean = false;

  public constructor(props: ProjectSpaceProviderContainerProps){
    super(props);
    this.state =  InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderProjectSpace({
        projectDetail: {
          usageInfo: this.state.projectInfo.usageInfo,
          usageInfoMaxValues: this.state.projectInfo.usageInfoMaxValues,
          ...this.state.projectDetail
        },
        projectId: this.state.projectDetail.id,
        openDetail: this.wrappedOpenContentDetailDialog,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        sanitizer: this.props.sanitizer
      });

    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        let errorCode = this.state.errorCode;
        if (!errorCode && this.state.projectInfo === null && !_.isUndefined(this.state.projectDetail)) {
          // projectInfo is null because getProjects returned no project. projectDetail _did_ return a project (it's not undefined anymore),
          // which means the project exists, the user just does not have permissions to view it. Set the error to insufficient permissions
          // manually so the user can request access to the project. TFS954605
          errorCode = CommonErrorCode.CallingUserInsufficientPermissions;
        }
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.projectId} contentType={'project'} errorCode={errorCode}/>;
      }
      return <RouterError errorType={'projects'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: ProjectSpaceProviderContainerProps) {
    if(this.changedProject(prevProps)) {
      this.props.setFetchedAllData(false);
      this.updateState(InitialState /* want the initial state for the new project */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.props.enterSpace(
        {
          item: this.state.projectDetail,
          spaceType: 'project',
          subspaceName: undefined
        }
      );

      this.props.requestSpaceContents();

      this.setState({initialRender: false, renderChildren: true});

      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.projectDetail.name });
    }
  }

  private getData = () => {
    this.getProjectInfo();
    this.getProjectDetail();
    this.getActions();
  }

  private wrappedOpenContentDetailDialog = () => {
    this.props.openContentDetailDialog(this.state.projectDetail, this.state.projectActions);
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.projectDetail);
  }

  private unfavorite = () => {
    this.props.removeContentFavorite(this.state.projectDetail);
  }

  private getProjectInfo = async () => {
    try {
      const project = await this.props.Projects.all()
        .withFilters([Filter.fieldEq('id', this.props.projectId)])
        .getFirst();

      if (!project) {
        this.updateState({hasError: true});
      }

      this.updateState({projectInfo: project});
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});
    }
  }

  private getProjectDetail = async () => {
    try {
      const detail = await this.props.Projects.getById(this.props.projectId);

      if (!detail) {
        this.updateState({hasError: true});
        return;
      }

      this.updateState({projectDetail: detail});
      const projectHierarchy = await this.props.Projects.getProjectHierarchy(detail.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      populateContentBreadcrumbs(projectHierarchyWithType, this.props.urls);
      this.props.setFetchedAllData(true);
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});

    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.Projects.actions(this.props.projectId);
      this.updateState({projectActions: actions});
    } catch (e) {}
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.projectInfo) &&
      !_.isUndefined(this.state.projectDetail) &&
      !this.state.hasError;
  }

  private changedProject(prevProps: ProjectSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.projectId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.projectId !== this.props.projectId);
  }

  private updateState<K extends keyof ProjectSpaceProviderContainerState>(newState: Pick<ProjectSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

const mapStateToProps: MapStateToProps<ProjectSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<ProjectSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
  openContentDetailDialog,
}, dispatch);

function ProjectSpaceServiceProvider(props: ProjectSpaceNonServicesProps) {
  return (<ProjectSpaceProviderContainer
    {...props}
    Projects={Projects}
    sanitizer={sanitize}
  />);
}

export const ConnectedProjectSpaceProviderContainer: React.ComponentClass<ProjectSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ProjectSpaceServiceProvider);

const renderProjectSpace = (props: ProjectSpaceOwnProps): JSX.Element => <ConnectedProjectSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}

export type ProjectSpaceProviderProps = LocationPathProp & Omit<ProjectSpaceProviderOwnProps, 'renderProjectSpace' | 'setFetchedAllData'>;

export interface ProjectSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export const ProjectSpaceProvider: React.FunctionComponent<ProjectSpaceProviderProps> = React.memo(props => {

  const [fetchedAllData, setFetchedAllData] = React.useState(false);
  const previousProjectId = usePrevious(props.projectId);

  // TFSID:983648.  Handle case when updating this component with a new projectId
  const resetStore = !_.isUndefined(previousProjectId) && !_.isUndefined(props.projectId)
    && previousProjectId !== props.projectId;

  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateContentTypes: true,
    shouldUpdateFilters: true,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: fetchedAllData,
    resetStore: resetStore
  };

  const ProjectSpaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'project',
    subspaceName: undefined
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={ProjectSpaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedProjectSpaceProviderContainer {...{...props, renderProjectSpace, setFetchedAllData: setFetchedAllData}}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
});
